import type {
  RunHorsesAction,
  RunHorsesState,
} from "@cloudnest/run-horses-engine";
import { validateAndReduceGameState } from "@cloudnest/run-horses-engine";
import { runHorsesEventToAction } from "./event-adapter";
import type { RunHorsesReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free 말달리자 (run-horses) replay simulation. `init` seeds canonical state;
 * every other event is reconstructed via the event adapter and folded through
 * the engine reducer (`validateAndReduceGameState`). Fails loudly when a fixture
 * event is unsupported or rejected by the engine, so a malformed fixture cannot
 * silently drop steps.
 *
 * Run-horses is complete information — there is no viewer-state redaction.
 * Imports only the engine: no React, stores, auth, router, or WebSocket.
 */
export interface RunHorsesReplaySnapshot {
  readonly index: number;
  readonly event: RunHorsesReplayFixtureEvent;
  /** The reconstructed engine action, or null for the `init` snapshot. */
  readonly action: RunHorsesAction | null;
  readonly state: RunHorsesState;
}

function requireState(
  state: RunHorsesState | null,
  eventType: string,
): RunHorsesState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

export function buildRunHorsesReplaySnapshots(
  events: readonly RunHorsesReplayFixtureEvent[],
): readonly RunHorsesReplaySnapshot[] {
  const snapshots: RunHorsesReplaySnapshot[] = [];
  let state: RunHorsesState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = (event.payload as { state: RunHorsesState }).state;
      snapshots.push({ index: snapshots.length, event, action: null, state });
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = runHorsesEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push({ index: snapshots.length, event, action, state });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty run-horses event log");
  }

  return snapshots;
}
